import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { CarouselResponse, CarouselRequest, BookResponse } from '@builtbybros/al-model';
import { DragulaService } from 'ng2-dragula';
import { Observable, Subject } from 'rxjs';

import { AlertService } from '../../shared/alert.service';
import { HttpService } from '../../shared/http.service';
import { FormsService } from '../../shared/forms.service';
import { QueryParam } from '../../shared/query-param';

@Component({
  selector: 'app-carousel-form',
  templateUrl: './carousel-form.component.html',
  styleUrls: ['./carousel-form.component.scss']
})
export class CarouselFormComponent implements OnInit {
  carouselId;
  carouselForm: FormGroup;
  carouselUrl = '/Carousel/';
  carouselReady = false;
  carouselResp;
  carouselRolesArr = [];

  books = [];
  btnMsg = 'Confirmar';
  h1 = 'Ranking';
  booksReady = false;
  bookUrl = '/Book/';

  searchResultItems: Observable<BookResponse.BookExcerpt[]>;
  private searchTerms = new Subject<string>();
  private searching = false;

  constructor(private route: ActivatedRoute,
              private formsService: FormsService,
              private httpService: HttpService,
              private alertService: AlertService,
              private fb: FormBuilder,
              private dragulaService: DragulaService) {
    dragulaService.drag.subscribe((value) => {
      this.onDrag(value.slice(1));
    });
    dragulaService.drop.subscribe((value) => {
      this.onDrop(value.slice(1));
    });
    dragulaService.over.subscribe((value) => {
      this.onOver(value.slice(1));
    });
    dragulaService.out.subscribe((value) => {
      this.onOut(value.slice(1));
    });
    this.createFormGroup();
  }

  ngOnInit() {
    this.route.params.subscribe((params: Params) => {
      if (params['carouselId']) {
        this.carouselId = +params['carouselId'];
        const responseExpected = new CarouselResponse.Carousel();
        this.httpService.get(this.carouselUrl, this.carouselId, responseExpected)
          .subscribe(
            carouselResponse => {
              this.carouselResp = carouselResponse;
              this.carouselRolesArr = carouselResponse.roles.split(',');
              this.carouselForm.patchValue(carouselResponse);
              this.carouselForm.get('publicCheckbox').setValue(this.carouselRolesArr.indexOf('2') !== -1);
              this.carouselForm.get('consigneeCheckbox').setValue(this.carouselRolesArr.indexOf('3') !== -1);
              this.carouselForm.get('schoolCheckbox').setValue(this.carouselRolesArr.indexOf('4') !== -1);
              this.books = carouselResponse.books;
              this.carouselReady = true;
            });
      } else {
        this.carouselReady = true;
      }
    });
    this.searchResultItems = this.searchTerms
      .debounceTime(300)        // wait 300ms after each keystroke before considering the term
      .distinctUntilChanged()   // ignore if next search term is same as previous
      .do(() => this.searching = true)
      .switchMap(term => term ? this.searchItem(term) : Observable.of<BookResponse.BookExcerpt[]>([]))
      .catch(error => {
        this.alertService.error(error);
        return Observable.of<BookResponse.BookExcerpt[]>([]);
      })
      .do(() => this.searching = false);
  }

  private onDrag(_args) {
    // let [e, el] = args;
    // do something
  }

  private onDrop(_args) {
    // let [e, el] = args;
    // do something
  }

  private onOver(_args) {
    // let [e, el, container] = args;
    // do something
  }

  private onOut(_args) {
    // let [e, el, container] = args;
    // do something
  }

  private booksToPatch() {
    let books = '';
    for (const b of this.books) {
      books += `,${b.id}`;
    }
    books = books.slice(1, books.length);
    return books;
  }

  private rolesToPatch() {
    let roles = '';
    roles += (this.carouselForm.get('publicCheckbox').value) ? '2,' : '';
    roles += (this.carouselForm.get('consigneeCheckbox').value) ? '3,' : '';
    roles += (this.carouselForm.get('schoolCheckbox').value) ? '4,' : '';
    if (roles.length) {
      roles = roles.slice(0, roles.length - 1);
    }
    return roles;
  }

  submitForm() {
    if (this.formsService.validateFormAndUpdateErrors(this.carouselForm)) {
      const expectedResponse = new CarouselResponse.Carousel();
      if (this.carouselId) {
        const carousel = new CarouselRequest.Update();
        carousel.parse(this.carouselForm.value);
        carousel.bookIds = this.booksToPatch();
        carousel.roles = this.rolesToPatch();
        if (!carousel.roles.length) {
          this.alertService.error('Debe elegirse por lo menos un rol');
        } else {
          this.httpService.patch(this.carouselId, this.carouselUrl, carousel, expectedResponse)
            .subscribe(
              _createResponse => {
                this.alertService.success('Carousel actualizado correctamente');
              },
              error => {
                this.alertService.error(error);
              }
            );
        }
      } else {
        const carousel = new CarouselRequest.Create();
        carousel.parse(this.carouselForm.value);
        carousel.bookIds = this.booksToPatch();
        carousel.roles = this.rolesToPatch();
        if (!carousel.roles.length) {
          this.alertService.error('Debe elegirse por lo menos un rol');
        } else {
          this.httpService.post(this.carouselUrl, carousel, expectedResponse)
            .subscribe(
              _createResponse => {
                this.alertService.success('carousel creado correctamente');
              },
              error => {
                this.alertService.error(error);
              }
            );
        }
      }
    }
  }

  private createFormGroup() {
    this.carouselForm = this.fb.group({
      active: [
        false,
        null
      ],
      consigneeCheckbox: [
        false,
        null
      ],
      name: [
        null,
        Validators.compose([
          Validators.required,
          Validators.maxLength(50)
        ])
      ],
      publicCheckbox: [
        false,
        null
      ],
      schoolCheckbox: [
        false,
        null
      ],
    });
  }

  addItem(value: number) {
    let responseExpected = new BookResponse.Book();
    this.httpService.get(this.bookUrl, value, responseExpected)
      .subscribe(
        createResponse => {
          if (createResponse) {
            this.books.push(createResponse);
          }
        }
      );
  }

  search(term: string): void {
    this.searchTerms.next(term);
  }

  private searchItem(query: string): Observable<BookResponse.BookExcerpt[]> {
    const params = new Array<QueryParam>();
    params[0] = new QueryParam('pagination', '10');
    params[1] = new QueryParam('query', query);

    const searchUrl = '/Book/All';
    const resultObservable: Observable<BookResponse.All> = this.httpService.getWithParams(
      searchUrl, params, BookResponse.All);

    return resultObservable
      .map(itemResponse => itemResponse.objects,
        _error => [],
      );
  }

  cleanSearch() {
    this.searchTerms.next('');
  }

  delete(id) {
    if (confirm('eliminar?')) {
      this.books.splice(this.books.findIndex((item) => {
        return item.id === id;
      }), 1);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/carousel/shared/carousel-form.component.ts